/**
 * The strategy composer: the stack being assembled, and whether it is saved.
 *
 * Deliberately thinner than `editor.ts`. A spread is built chip by chip and
 * needs fifty steps of undo; a strategy holds at most six layers and every
 * change is one click to reverse, so history here would be ceremony. What it
 * does need is the same dirty-tracking discipline, because a strategy you
 * forgot to save is exactly as lost as a spread you forgot to save.
 *
 * Layer *order* lives here and nowhere else, which is what makes the colour
 * ramp and the per-layer attribution agree with each other without either
 * having to be told.
 */

import { createSignal } from 'solid-js';
import type { Spread, WheelStyle } from '~/domain/types';
import {
  addLayer,
  canAddSpread,
  createStrategy,
  moveLayer,
  removeLayer,
  type AddCheck,
  type Strategy,
} from '~/domain/strategy';

export interface StrategyComposer {
  strategy: () => Strategy;
  name: () => string;
  wheelStyle: () => WheelStyle;
  strategyId: () => string;
  dirty: () => boolean;
  /** Whether a catalogue row can join, and why not when it cannot. */
  canAdd: (spread: Spread) => AddCheck;

  setName: (name: string) => void;
  addSpread: (spread: Spread) => void;
  removeSpread: (spreadId: string) => void;
  reorder: (from: number, to: number) => void;

  loadStrategy: (strategy: Strategy) => void;
  newStrategy: (style: WheelStyle) => void;
  toStrategy: () => Strategy;
  markSaved: () => void;
}

export function createStrategyComposer(
  style: WheelStyle = 'single-zero',
): StrategyComposer {
  const [strategy, setStrategy] = createSignal<Strategy>(
    createStrategy('Untitled strategy', style),
  );
  const [dirty, setDirty] = createSignal(false);

  /**
   * Every mutation goes through here, so "did anything change" is decided in
   * one place. `addLayer` and friends return the strategy unchanged when they
   * refuse, and comparing identity is how a refusal stays out of the dirty
   * flag — a rejected click should not make a saved strategy look unsaved.
   */
  const apply = (next: Strategy) => {
    if (next === strategy()) return;
    setStrategy(next);
    setDirty(true);
  };

  return {
    strategy,
    name: () => strategy().name,
    wheelStyle: () => strategy().wheelStyle,
    strategyId: () => strategy().id,
    dirty,
    canAdd: (spread) => canAddSpread(strategy(), spread),

    setName: (name) => {
      if (name === strategy().name) return;
      apply({ ...strategy(), name, updatedAt: Date.now() });
    },
    addSpread: (spread) => apply(addLayer(strategy(), spread)),
    removeSpread: (spreadId) => apply(removeLayer(strategy(), spreadId)),
    reorder: (from, to) => apply(moveLayer(strategy(), from, to)),

    loadStrategy: (loaded) => {
      setStrategy(loaded);
      setDirty(false);
    },
    newStrategy: (nextStyle) => {
      setStrategy(createStrategy('Untitled strategy', nextStyle));
      setDirty(false);
    },
    toStrategy: () => strategy(),
    markSaved: () => setDirty(false),
  };
}
